import { buyerCaptureException } from '@diamond/shared/utils';
import { clearPwaSessionCaches, usePwaStore } from '@diamond/sol-buyer-context';
import { useEffect } from 'react';

let registrationPromise: Promise<ServiceWorkerRegistration> | null = null;
let runtimeSyncPromise: Promise<void> = Promise.resolve();
let enabledRuntimePromise: Promise<void> | null = null;
let readyTimeoutId: number | undefined;

const REQUIRED_OFFLINE_ASSET_URLS = [
  '/offline.html',
  '/images/sukanda_logo_LR.png',
] as const;
const SERVICE_WORKER_READY_TIMEOUT_MS = 60_000;

type PwaOperation = 'activation' | 'offline_precache_recovery' | 'registration';

class PwaRuntimeSyncError extends Error {
  constructor(message: string, readonly operation: PwaOperation) {
    super(message);
    this.name = 'PwaRuntimeSyncError';
  }
}

function isSolBuyerServiceWorker(registration: ServiceWorkerRegistration) {
  const worker =
    registration.active || registration.waiting || registration.installing;
  if (!worker) return false;

  const workerUrl = new URL(worker.scriptURL);
  return (
    workerUrl.origin === window.location.origin &&
    (workerUrl.pathname.endsWith('/sw.js') ||
      workerUrl.pathname.includes('/swe-worker-'))
  );
}

export function resetPwaServiceWorkerRuntime() {
  if (readyTimeoutId !== undefined) {
    window.clearTimeout(readyTimeoutId);
    readyTimeoutId = undefined;
  }
  registrationPromise = null;
  runtimeSyncPromise = Promise.resolve();
  enabledRuntimePromise = null;
}

async function hasRequiredOfflinePrecacheAssets() {
  const cacheStorage = window.caches;
  if (typeof cacheStorage?.keys !== 'function') return false;

  try {
    const cacheNames = await cacheStorage.keys();
    const precacheNames = cacheNames.filter((cacheName) => {
      const normalizedName = cacheName.toLowerCase();
      return (
        normalizedName.includes('serwist') &&
        normalizedName.includes('precache')
      );
    });

    for (const cacheName of precacheNames) {
      const precache = await cacheStorage.open(cacheName);
      const cachedAssets = await Promise.all(
        REQUIRED_OFFLINE_ASSET_URLS.map((url) =>
          precache.match(url, { ignoreSearch: true })
        )
      );

      if (cachedAssets.every(Boolean)) return true;
    }
  } catch {
    return false;
  }

  return false;
}

function waitForServiceWorkerReady() {
  const ready = navigator.serviceWorker?.ready;
  if (!ready) {
    return Promise.reject(
      new PwaRuntimeSyncError(
        'The service worker ready promise is unavailable',
        'activation'
      )
    );
  }

  return new Promise<ServiceWorkerRegistration>((resolve, reject) => {
    const timeoutId = window.setTimeout(() => {
      if (readyTimeoutId === timeoutId) {
        readyTimeoutId = undefined;
      }
      reject(
        new PwaRuntimeSyncError(
          'Timed out waiting for the SOL Buyer service worker to activate',
          'activation'
        )
      );
    }, SERVICE_WORKER_READY_TIMEOUT_MS);
    readyTimeoutId = timeoutId;

    void ready.then(
      (registration) => {
        window.clearTimeout(timeoutId);
        if (readyTimeoutId === timeoutId) {
          readyTimeoutId = undefined;
        }
        resolve(registration);
      },
      (error) => {
        window.clearTimeout(timeoutId);
        if (readyTimeoutId === timeoutId) {
          readyTimeoutId = undefined;
        }
        reject(error);
      }
    );
  });
}

async function waitForSolBuyerActivation(
  registration: ServiceWorkerRegistration
) {
  if (registration.active) return registration;

  const readyRegistration = await waitForServiceWorkerReady();
  if (
    !readyRegistration.active ||
    !isSolBuyerServiceWorker(readyRegistration)
  ) {
    throw new PwaRuntimeSyncError(
      'The ready service worker is not an active SOL Buyer worker',
      'activation'
    );
  }

  return readyRegistration;
}

async function installAndVerifyPwaServiceWorker() {
  await navigator.serviceWorker.register('/sw.js', { scope: '/' });
  const readyRegistration = await waitForServiceWorkerReady();

  if (
    !readyRegistration.active ||
    !isSolBuyerServiceWorker(readyRegistration)
  ) {
    throw new PwaRuntimeSyncError(
      'The recovered service worker did not become active',
      'activation'
    );
  }

  if (!(await hasRequiredOfflinePrecacheAssets())) {
    throw new PwaRuntimeSyncError(
      'The recovered service worker is missing required offline precache assets',
      'offline_precache_recovery'
    );
  }

  return readyRegistration;
}

function registerPwaServiceWorker() {
  if (!('serviceWorker' in navigator)) return Promise.resolve();
  if (registrationPromise) return registrationPromise;

  registrationPromise = (async () => {
    const existingRegistration = await navigator.serviceWorker.getRegistration(
      '/'
    );

    if (existingRegistration && isSolBuyerServiceWorker(existingRegistration)) {
      const activeRegistration = await waitForSolBuyerActivation(
        existingRegistration
      );
      if (await hasRequiredOfflinePrecacheAssets()) {
        return activeRegistration;
      }

      await existingRegistration.unregister();
    }

    try {
      return await installAndVerifyPwaServiceWorker();
    } catch (error) {
      if (error instanceof PwaRuntimeSyncError) throw error;

      const message =
        error instanceof Error
          ? error.message
          : 'Unknown service worker registration error';
      throw new PwaRuntimeSyncError(message, 'registration');
    }
  })();

  void registrationPromise.then(
    () => {
      registrationPromise = null;
    },
    () => {
      registrationPromise = null;
    }
  );

  return registrationPromise;
}

function reportPwaRuntimeFailure(error: unknown) {
  let runtimeError: PwaRuntimeSyncError;
  if (error instanceof PwaRuntimeSyncError) {
    runtimeError = error;
  } else {
    const message =
      error instanceof Error ? error.message : 'Unknown PWA runtime error';
    runtimeError = new PwaRuntimeSyncError(message, 'registration');
  }

  buyerCaptureException(
    {
      message: runtimeError.message,
      name: runtimeError.name,
    },
    (scope) => {
      scope.setTag('feature', 'pwa');
      scope.setTag('pwa_operation', runtimeError.operation);
    }
  );
}

function ensureEnabledPwaRuntime() {
  if (enabledRuntimePromise) return enabledRuntimePromise;

  enabledRuntimePromise = registerPwaServiceWorker()
    .then(() => undefined)
    .catch((error) => {
      reportPwaRuntimeFailure(error);
      throw error;
    });

  void enabledRuntimePromise.then(
    () => {
      enabledRuntimePromise = null;
    },
    () => undefined
  );

  return enabledRuntimePromise;
}

async function unregisterPwaServiceWorkers() {
  if (!('serviceWorker' in navigator)) return;

  await enabledRuntimePromise?.catch(() => undefined);
  enabledRuntimePromise = null;
  await registrationPromise?.catch(() => undefined);

  const registrations = await navigator.serviceWorker.getRegistrations();
  await Promise.allSettled(
    registrations
      .filter(isSolBuyerServiceWorker)
      .map((registration) => registration.unregister())
  );
}

function waitUntilDocumentComplete(signal: AbortSignal) {
  if (signal.aborted || document.readyState === 'complete') {
    return Promise.resolve();
  }

  return new Promise<void>((resolve) => {
    const finish = () => {
      window.removeEventListener('load', finish);
      document.removeEventListener('readystatechange', onReadyStateChange);
      signal.removeEventListener('abort', finish);
      resolve();
    };
    const onReadyStateChange = () => {
      if (document.readyState === 'complete') finish();
    };

    window.addEventListener('load', finish, { once: true });
    document.addEventListener('readystatechange', onReadyStateChange);
    signal.addEventListener('abort', finish, { once: true });

    if (document.readyState === 'complete' || signal.aborted) finish();
  });
}

function canUseRequestIdleCallback() {
  return (
    typeof window.requestIdleCallback === 'function' &&
    !/jsdom/i.test(window.navigator.userAgent)
  );
}

function waitUntilIdle(signal: AbortSignal) {
  if (signal.aborted) return Promise.resolve();

  if (canUseRequestIdleCallback()) {
    return new Promise<void>((resolve) => {
      let idleId = 0;
      const onAbort = () => {
        window.cancelIdleCallback(idleId);
        resolve();
      };
      idleId = window.requestIdleCallback(() => {
        signal.removeEventListener('abort', onAbort);
        resolve();
      });
      signal.addEventListener('abort', onAbort, { once: true });
    });
  }

  return new Promise<void>((resolve) => {
    let timeoutId = 0;
    const onAbort = () => {
      window.clearTimeout(timeoutId);
      resolve();
    };
    timeoutId = window.setTimeout(() => {
      signal.removeEventListener('abort', onAbort);
      resolve();
    }, 0);
    signal.addEventListener('abort', onAbort, { once: true });
  });
}

function waitUntilFirstPaintQuiet(signal: AbortSignal) {
  return waitUntilDocumentComplete(signal).then(() => waitUntilIdle(signal));
}

function enqueueRuntimeSync(task: () => Promise<void>) {
  runtimeSyncPromise = runtimeSyncPromise.catch(() => undefined).then(task);
  return runtimeSyncPromise;
}

function reportRuntimeSyncError(error: unknown) {
  console.error('Error syncing PWA runtime:', error);
}

export function usePwaServiceWorker(isEnabled: boolean, isLoading: boolean) {
  const setInstallPromptEvent = usePwaStore(
    (state) => state.setInstallPromptEvent
  );
  const setIsInstallable = usePwaStore((state) => state.setIsInstallable);
  const setIsInstalled = usePwaStore((state) => state.setIsInstalled);

  useEffect(() => {
    if (isLoading) return;

    const abort = new AbortController();

    if (!isEnabled) {
      enqueueRuntimeSync(async () => {
        setInstallPromptEvent(null);
        setIsInstallable(false);
        setIsInstalled(false);
        await unregisterPwaServiceWorkers();
        await clearPwaSessionCaches();
      }).catch((error) => {
        if (!abort.signal.aborted) reportRuntimeSyncError(error);
      });

      return () => abort.abort();
    }

    void waitUntilFirstPaintQuiet(abort.signal).then(() => {
      if (abort.signal.aborted) return;
      enqueueRuntimeSync(() => ensureEnabledPwaRuntime()).catch((error) => {
        if (!abort.signal.aborted) reportRuntimeSyncError(error);
      });
    });

    return () => abort.abort();
  }, [
    isEnabled,
    isLoading,
    setInstallPromptEvent,
    setIsInstallable,
    setIsInstalled,
  ]);
}
